import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { AnalysisChoice, JiraAnalysis, JiraDeepAnalysis, LinkedTicket } from '../api/types.js';
import { useAnalysisDecisions } from '../hooks/use-sprint.js';
import { byUrgency, urgencyOf } from '../utils/urgency.js';
import { useHiddenInbox, useSetAside } from '../hooks/use-inbox.js';
import { hiddenLine } from '../utils/set-aside.js';
import { SetAsideForm } from './set-aside-form.js';

// The tickets that came into a board, as its sprint session read them (ADR-034 step 3a): one line each, opened to decide.
// The changes proposed are a checklist, each editable. The three decisions of ADR-039: Approve writes the ones kept to
// Jira under the operator's account, Reject writes nothing, Set aside says what is missing (the drafted comment to the
// author is the question, posted on its own button). Members read, admins decide.

const POINTS = [0.5, 1, 2, 3, 5, 8, 13, 21];
type Field = keyof AnalysisChoice;
const LABELS: Record<Field, string> = {
  summary: 'Summary', description: 'Add to the description', team: 'Requesting team', parent: 'Parent', storyPoints: 'Story points', sprint: 'Sprint',
  assignee: 'Assignee',
};
const REASONS: Record<JiraAnalysis['reason'], string> = {
  entered: 'came in', changed: 'priority or due date changed', asked: 'asked', assigned: 'assigned to you', mentioned: 'mentions you',
};
const FIELDS: Field[] = ['assignee', 'summary', 'description', 'team', 'parent', 'storyPoints', 'sprint'];

/** Who created the ticket and who has it, after when it was created: `assigned to you` is said by the reason already */
function whoLine(a: JiraAnalysis): string {
  const by = a.reporter ? ` by ${a.reporter}` : '';
  const has = a.reason === 'assigned' ? '' : a.assignedTo ? `, assigned to ${a.assignedTo}` : a.reporter ? ', unassigned' : '';
  return `${by}${has}`;
}

/** The sprint the ticket is in now (read live for an open one on a board), or that it is in none */
function sprintOf(a: JiraAnalysis): string | null {
  if (a.inSprint) return a.inSprint.coming ? `planned in ${a.inSprint.name}` : `in sprint ${a.inSprint.name}`;
  return a.boardId && a.status !== 'applied' ? 'no sprint' : null;
}

// What the operator does with a ticket, which is how the list is grouped
type Group = 'apply' | 'ask' | 'running' | 'decided';
const GROUPS: Array<{ id: Group; title: string }> = [
  { id: 'apply', title: 'Ready to apply' },
  { id: 'ask', title: 'Information missing' },
  { id: 'running', title: 'Being analysed' },
  { id: 'decided', title: 'Decided this week' },
];

function groupOf(a: JiraAnalysis): Group {
  if (a.status === 'queued' || a.status === 'applying') return 'running';
  if (a.status === 'applied') return 'decided';
  return a.explicit ? 'apply' : 'ask';
}

/** What the analysis proposes, in a few words, so the list can be read without opening each ticket */
function digest(a: JiraAnalysis): { text: string; tone: string } {
  if (a.status === 'failed') return { text: 'not applied, open to retry', tone: 'var(--color-error)' };
  const g = groupOf(a);
  if (g === 'running' || g === 'decided') return verdict(a);
  if (g === 'ask') {
    const n = a.missing?.length ?? 0;
    const who = a.reason === 'assigned' || a.reason === 'mentioned' ? `reply to ${a.reporter ?? 'the author'}` : `ask ${a.reporter ?? 'the author'}`;
    return { text: n ? `${n} ${n === 1 ? 'point' : 'points'} missing, ${who}` : who, tone: 'var(--color-warn)' };
  }
  const parts = [
    a.assignee ? `to ${a.assignee}` : '',
    a.storyPoints !== undefined ? `${a.storyPoints} SP` : '',
    a.sprint ? a.sprint : '',
    a.parent ? `under ${a.parent}` : '',
    a.team ? a.team : '',
    a.summary || a.description ? 'wording' : '',
  ].filter(Boolean);
  const mine = a.reason === 'assigned' || a.reason === 'mentioned';
  const said = a.askAuthor && !a.askedAt ? (mine ? 'reply ready' : 'comment ready') : '';
  return { text: [...parts, said].filter(Boolean).join(', ') || 'nothing to change', tone: 'var(--color-text-2)' };
}

const input = 'w-full rounded-md border px-2 py-1 text-sm';
const inputStyle = { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' };
const button = 'rounded-md border px-2.5 py-1 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60';
const primary = { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-text)', outlineColor: 'var(--color-brand)' };
const quiet = { borderColor: 'var(--color-border)', color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' };

function verdict(a: JiraAnalysis): { text: string; tone: string } {
  if (a.status === 'queued') return { text: 'being analysed', tone: 'var(--color-text-3)' };
  if (a.status === 'applying') return { text: 'writing to Jira…', tone: 'var(--color-text-3)' };
  if (a.status === 'applied') return { text: `applied: ${(a.applied ?? []).map((f) => LABELS[f as Field]?.toLowerCase() ?? f).join(', ') || 'nothing'}`, tone: 'var(--color-success)' };
  if (a.status === 'failed') return { text: 'not applied', tone: 'var(--color-error)' };
  return a.explicit ? { text: 'ready to apply', tone: 'var(--color-text-2)' } : { text: 'information missing', tone: 'var(--color-warn)' };
}

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** The plan as a comment for the ticket, the way the API posts it; editable before posting */
function planComment(d: JiraDeepAnalysis): string {
  const items = (title: string, xs?: string[]) => (xs?.length ? `${title}\n${xs.map((x) => `- ${x}`).join('\n')}` : '');
  return [
    d.summary,
    d.approach ? `One way to do it:\n${d.approach}` : '',
    d.options?.length ? `Options:\n${d.options.map((o) => `- ${o.name}: ${o.tradeoffs}`).join('\n')}` : '',
    items('Files concerned:', d.files),
    items('Risks:', d.risks),
    items('Open questions:', d.questions),
    d.storyPoints !== undefined ? `Estimate: ${d.storyPoints} SP${d.storyPointsReason ? `, ${d.storyPointsReason}` : ''}` : '',
  ].filter(Boolean).join('\n\n');
}

/** The deep analysis once done: what the request is, one way to do it, what to weigh, and the plan to post */
/** Implement (ADR-034 step 5b): the plan's approach, edited, in a repository the server works on; the ticket then follows the GitHub tickets' circuit */
interface ImplementProps { pending: boolean; error?: string; started?: string; onImplement: (repository: string, approach: string) => void }

function Plan({ id, d, isAdmin, onEstimate, onPost, posting, postError, discuss, implement }: {
  id: string; d: JiraDeepAnalysis; discuss?: string; isAdmin: boolean; onEstimate?: () => void; onPost: (comment: string) => void; posting: boolean; postError?: string;
  implement?: ImplementProps;
}) {
  const [comment, setComment] = useState(() => planComment(d));
  const [repository, setRepository] = useState(d.repository ?? '');
  const [approach, setApproach] = useState(d.approach ?? '');
  const [implementing, setImplementing] = useState(false);
  const list = (title: string, xs?: string[]) => (xs?.length ? (
    <div>
      <p style={{ color: 'var(--color-text-2)' }}>{title}</p>
      <ul className="ml-4 list-disc" style={{ color: 'var(--color-text)' }}>{xs.map((x) => <li key={x} className="break-words">{x}</li>)}</ul>
    </div>
  ) : null);
  return (
    <section className="space-y-3 border-l-2 pl-3" style={{ borderColor: 'var(--color-brand)' }} aria-label="Deep analysis">
      <p className="flex flex-wrap gap-x-3 text-xs" style={{ color: 'var(--color-text-3)' }}>
        <span>{`Deep analysis against the code${d.doneAt ? `, ${when(d.doneAt)}` : ''}`}</span>
        {discuss && <Link to={discuss} className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>Discuss the plan in its session</Link>}
      </p>
      {d.summary && <p className="max-w-[72ch]" style={{ color: 'var(--color-text)' }}>{d.summary}</p>}
      {d.approach && (
        <div>
          <p style={{ color: 'var(--color-text-2)' }}>One way to do it</p>
          <p className="max-w-[72ch] whitespace-pre-wrap" style={{ color: 'var(--color-text)' }}>{d.approach}</p>
        </div>
      )}
      {d.options && d.options.length > 0 && (
        <div>
          <p style={{ color: 'var(--color-text-2)' }}>Options</p>
          <ul className="ml-4 list-disc" style={{ color: 'var(--color-text)' }}>{d.options.map((o) => <li key={o.name}><span className="font-medium">{o.name}</span>{`: ${o.tradeoffs}`}</li>)}</ul>
        </div>
      )}
      {list('Files concerned', d.files)}
      {list('Risks', d.risks)}
      {list('Open questions', d.questions)}
      {d.storyPoints !== undefined && (
        <p className="flex flex-wrap items-baseline gap-x-3" style={{ color: 'var(--color-text)' }}>
          <span><span className="font-medium tabular-nums">{`${d.storyPoints} SP`}</span>{d.storyPointsReason ? `, ${d.storyPointsReason}` : ''}</span>
          {onEstimate && <button type="button" className="text-sm underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }} onClick={onEstimate}>Use this estimate</button>}
        </p>
      )}
      <div className="space-y-1.5">
        <label htmlFor={id} style={{ color: 'var(--color-text-2)' }}>{d.postedAt ? `Posted to the ticket on ${when(d.postedAt)}` : 'Post the plan to the ticket'}</label>
        {!d.postedAt && (
          <>
            <textarea id={id} rows={8} className={input} style={inputStyle} disabled={!isAdmin} value={comment} onChange={(e) => setComment(e.target.value)} />
            {isAdmin && (
              <button type="button" className={button} style={quiet} disabled={!comment.trim() || posting} onClick={() => onPost(comment)}>
                {posting ? 'Posting…' : 'Post the plan'}
              </button>
            )}
          </>
        )}
        {postError && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Not posted: ${postError}`}</p>}
      </div>
      {implement && isAdmin && (
        <div className="space-y-1.5" data-testid="jira-implement">
          {implement.started ? (
            <p style={{ color: 'var(--color-text-2)' }}>
              Implementation started: its change comes back on the <Link to={implement.started} className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>ticket's page</Link>, to open the pull request from.
            </p>
          ) : !implementing ? (
            <button type="button" className={button} style={quiet} onClick={() => setImplementing(true)}>Implement</button>
          ) : (
            <>
              <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>The ticket's session implements the approach below in the repository, on a local branch; the change comes back here for you to open the pull request. Nothing is pushed before.</p>
              <label htmlFor={`${id}-repo`} className="text-xs" style={{ color: 'var(--color-text-3)' }}>Repository</label>
              <input id={`${id}-repo`} className={input} style={inputStyle} value={repository} onChange={(e) => setRepository(e.target.value)} placeholder="org/repo" />
              <label htmlFor={`${id}-approach`} className="text-xs" style={{ color: 'var(--color-text-3)' }}>Approach</label>
              <textarea id={`${id}-approach`} rows={8} className={input} style={inputStyle} value={approach} onChange={(e) => setApproach(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                <button type="button" className={button} style={primary} disabled={!/^[A-Za-z0-9][A-Za-z0-9-]*\/[A-Za-z0-9_][A-Za-z0-9_.-]*$/.test(repository.trim()) || !approach.trim() || implement.pending} onClick={() => implement.onImplement(repository.trim(), approach)}>
                  {implement.pending ? 'Starting…' : 'Implement this approach'}
                </button>
                <button type="button" className={button} style={quiet} onClick={() => setImplementing(false)}>Cancel</button>
              </div>
            </>
          )}
          {implement.error && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Not started: ${implement.error}`}</p>}
        </div>
      )}
    </section>
  );
}

/** The ticket a developers' ticket asks for in the infrastructure project (ADR-034 section 3): edited, then created and
 * linked under the operator's account */
function LinkedTicketForm({ serverId, a, isAdmin }: { serverId: string; a: JiraAnalysis; isAdmin: boolean }) {
  const decisions = useAnalysisDecisions(serverId);
  const [t, setT] = useState<LinkedTicket>(() => a.infraTicket ?? { summary: '' });
  const created = a.linkedCreated && a.linkedCreated !== '…' ? a.linkedCreated : undefined;
  const locked = !isAdmin || !!a.linkedCreated || decisions.linked.isPending;
  const field = (f: keyof LinkedTicket, label: string) => (
    <div className="space-y-0.5">
      <label htmlFor={`${a.key}-linked-${f}`} className="text-xs" style={{ color: 'var(--color-text-3)' }}>{label}</label>
      {f === 'description'
        ? <textarea id={`${a.key}-linked-${f}`} rows={3} className={input} style={inputStyle} disabled={locked} value={t[f] ?? ''} onChange={(e) => setT({ ...t, [f]: e.target.value })} />
        : <input id={`${a.key}-linked-${f}`} className={input} style={inputStyle} disabled={locked} value={t[f] ?? ''} onChange={(e) => setT({ ...t, [f]: e.target.value })} />}
    </div>
  );
  return (
    <div className="space-y-1.5" data-testid="linked-ticket">
      <p style={{ color: 'var(--color-text-2)' }}>
        {created ? <>Linked ticket created: <a href={`${new URL(a.url).origin}/browse/${created}`} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{created}</a></>
          : a.linkedCreated ? 'Creating the linked ticket…' : 'This ticket asks for infrastructure work: the linked ticket to create'}
      </p>
      {!created && (
        <>
          {field('summary', 'Summary')}
          {field('description', 'Description')}
          <div className="grid gap-1.5 sm:grid-cols-2">{field('team', 'Requesting team')}{field('parent', 'Parent')}</div>
          {isAdmin && !a.linkedCreated && (
            <button type="button" className={button} style={quiet} disabled={!t.summary.trim() || decisions.linked.isPending}
              onClick={() => decisions.linked.mutate({ key: a.key, ticket: Object.fromEntries(Object.entries(t).filter(([, v]) => typeof v === 'string' && v.trim())) as unknown as LinkedTicket })}>
              {decisions.linked.isPending ? 'Creating…' : 'Create the linked ticket'}
            </button>
          )}
        </>
      )}
      {decisions.linked.isError && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Not created: ${decisions.linked.error.message}`}</p>}
    </div>
  );
}

function Decision({ serverId, a, isAdmin, sprints }: { serverId: string; a: JiraAnalysis; isAdmin: boolean; sprints: string[] }) {
  const decisions = useAnalysisDecisions(serverId);
  const [settingAside, setSettingAside] = useState(false);
  const [wasSetAside, setWasSetAside] = useState(false);
  // Set aside, from here or from « Waiting on you »: said here too, with the way back
  const aside = useHiddenInbox(serverId).data?.find((i) => i.id === `jira:${a.key}`);
  const bringBack = useSetAside(serverId);
  const deep = a.deep;
  // The operator's own ticket (ADR-034 step 5): the comment is his reply
  const mine = a.reason === 'assigned' || a.reason === 'mentioned';
  // The deep analysis' estimate makes the story points a change to decide on, even when the first analysis kept them
  const proposed = FIELDS.filter((f) => a[f] !== undefined || (f === 'storyPoints' && deep?.storyPoints !== undefined));
  const [values, setValues] = useState<AnalysisChoice>(() => Object.fromEntries(proposed.map((f) => [f, a[f] ?? (f === 'storyPoints' ? deep?.storyPoints : undefined)])) as AnalysisChoice);
  const [kept, setKept] = useState<Set<Field>>(() => new Set(proposed.filter((f) => a[f] !== undefined)));
  const [comment, setComment] = useState(a.askAuthor ?? '');
  const open = a.status === 'pending' || a.status === 'failed';
  const set = (f: Field, v: string | number | undefined) => setValues((old) => ({ ...old, [f]: v }));
  const toggle = (f: Field) => setKept((old) => { const next = new Set(old); if (next.has(f)) next.delete(f); else next.add(f); return next; });
  const choice = Object.fromEntries([...kept].map((f) => [f, values[f]]).filter(([, v]) => v !== undefined && v !== '')) as AnalysisChoice;
  const editor = (f: Field) => {
    const id = `${a.key}-${f}`;
    const disabled = !isAdmin || !open || !kept.has(f);
    if (f === 'description') return <textarea id={id} rows={3} className={input} style={inputStyle} disabled={disabled} value={values.description ?? ''} onChange={(e) => set(f, e.target.value)} />;
    if (f === 'storyPoints') {
      return (
        <select id={id} className={input} style={inputStyle} disabled={disabled} value={values.storyPoints ?? ''} onChange={(e) => set(f, e.target.value ? Number(e.target.value) : undefined)}>
          {POINTS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      );
    }
    if (f === 'assignee') {
      return (
        <select id={id} className={input} style={inputStyle} disabled={disabled} value={values.assignee ?? ''} onChange={(e) => set(f, e.target.value)}>
          {[...new Set([...(values.assignee ? [values.assignee] : []), ...(a.people ?? [])])].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      );
    }
    if (f === 'sprint' && sprints.length) {
      return (
        <select id={id} className={input} style={inputStyle} disabled={disabled} value={values.sprint ?? ''} onChange={(e) => set(f, e.target.value)}>
          {[...new Set([...(values.sprint ? [values.sprint] : []), ...sprints])].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }
    return <input id={id} className={input} style={inputStyle} disabled={disabled} value={String(values[f] ?? '')} onChange={(e) => set(f, e.target.value)} />;
  };
  // The first thing to do comes first: the changes when the ticket is clear, the question when it is not
  const asking = !a.explicit && !!(a.askAuthor || a.askedAt);
  const missing = a.missing && a.missing.length > 0 && (
    <div>
      <p style={{ color: 'var(--color-text-2)' }}>What is missing</p>
      <ul className="ml-4 list-disc" style={{ color: 'var(--color-text)' }}>{a.missing.map((m) => <li key={m}>{m}</li>)}</ul>
    </div>
  );
  const ask = (a.askAuthor || a.askedAt) && (
    <div className="space-y-1.5">
      <label htmlFor={`${a.key}-ask`} style={{ color: 'var(--color-text-2)' }}>{mine ? (a.askedAt ? 'Replied' : 'Reply') : a.askedAt ? 'Asked the author' : 'Ask the author'}</label>
      <textarea id={`${a.key}-ask`} rows={asking ? 5 : 3} className={input} style={inputStyle} disabled={!isAdmin || !!a.askedAt} value={comment} onChange={(e) => setComment(e.target.value)} />
      {isAdmin && !a.askedAt && (
        <button type="button" className={button} style={asking ? primary : quiet} disabled={!comment.trim() || decisions.ask.isPending} onClick={() => decisions.ask.mutate({ key: a.key, comment })}>
          {decisions.ask.isPending ? 'Posting…' : mine ? 'Post the reply' : 'Post the comment'}
        </button>
      )}
      {decisions.ask.isError && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Not posted: ${decisions.ask.error.message}`}</p>}
    </div>
  );
  const changes = proposed.length > 0 && (
    <fieldset className="space-y-2" disabled={!isAdmin || !open}>
      <legend className="mb-1" style={{ color: 'var(--color-text-2)' }}>{asking ? 'Changes proposed meanwhile' : 'Proposed changes'}</legend>
      {proposed.map((f) => (
        <div key={f} className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2">
          <input type="checkbox" className="mt-1.5" checked={kept.has(f)} onChange={() => toggle(f)} aria-label={`Keep ${LABELS[f].toLowerCase()}`} />
          <div>
            <label htmlFor={`${a.key}-${f}`} className="text-xs" style={{ color: 'var(--color-text-3)' }}>{LABELS[f]}</label>
            {editor(f)}
            {f === 'storyPoints' && a.storyPointsReason && <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>{a.storyPointsReason}</p>}
            {f === 'assignee' && a.assigneeReason && <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>{a.assigneeReason}</p>}
            {f === 'sprint' && a.sprintReason && <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>{a.sprintReason}</p>}
          </div>
        </div>
      ))}
    </fieldset>
  );
  const actions = isAdmin && open && (
    <div className="flex flex-wrap gap-2">
      {proposed.length > 0 && (
        <button type="button" className={button} style={asking ? quiet : primary} disabled={!Object.keys(choice).length || decisions.apply.isPending}
          onClick={() => decisions.apply.mutate({ key: a.key, choice })}>
          {decisions.apply.isPending ? 'Writing to Jira…' : 'Approve the selected changes'}
        </button>
      )}
      <button type="button" className={button} style={quiet} disabled={decisions.dismiss.isPending} onClick={() => decisions.dismiss.mutate(a.key)}>Reject</button>
      <button type="button" className={button} style={quiet} aria-expanded={settingAside} onClick={() => { setSettingAside(!settingAside); setWasSetAside(false); }}>Set aside</button>
      {deep?.status !== 'queued' && deep?.status !== 'done' && (
        <button type="button" className={button} style={quiet} disabled={decisions.deep.isPending} onClick={() => decisions.deep.mutate(a.key)}>
          {decisions.deep.isPending ? 'Sending…' : 'Deep analysis'}
        </button>
      )}
    </div>
  );
  const why = a.notes && (
    <div>
      <p style={{ color: 'var(--color-text-2)' }}>Why</p>
      <p className="max-w-[72ch]" style={{ color: 'var(--color-text-2)' }}>{a.notes}</p>
    </div>
  );
  return (
    <div className="mb-4 mt-1 max-w-2xl space-y-3 border-l-2 pl-3 text-sm" style={{ borderColor: 'var(--color-brand)' }}>
      {a.status === 'failed' && a.error && <p role="alert" style={{ color: 'var(--color-error)' }}>{a.error}</p>}
      {asking ? <>{ask}{missing}</> : null}
      {changes}
      {actions}
      {settingAside && isAdmin && open && <SetAsideForm serverId={serverId} itemId={`jira:${a.key}`} onDone={() => { setSettingAside(false); setWasSetAside(true); }} onCancel={() => setSettingAside(false)} />}
      {aside && !settingAside && (
        <p role="status" className="flex flex-wrap items-baseline gap-x-2 text-xs" style={{ color: 'var(--color-text-2)' }}>
          <span>{aside.hidden.state === 'set-aside' && !aside.hidden.byAidev && aside.hidden.reason ? 'Set aside. ' : ''}{hiddenLine(aside.hidden)}.</span>
          {isAdmin && (
            <button type="button" className="hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}
              disabled={bringBack.isPending} onClick={() => bringBack.mutate({ itemId: aside.id, action: 'bring-back' })}>
              Bring back
            </button>
          )}
        </p>
      )}
      {!aside && wasSetAside && !settingAside && <p role="status" className="text-xs" style={{ color: 'var(--color-text-2)' }}>Set aside: it comes back in Waiting on you when the ticket changes.</p>}
      {decisions.apply.isError && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Not applied: ${decisions.apply.error.message}`}</p>}
      {decisions.dismiss.isError && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Not rejected: ${decisions.dismiss.error.message}`}</p>}
      {why}
      {!asking && missing}
      {!asking && ask}
      {/* The ticket's own session, given the ticket and its analysis: not the sprint session, where it was one among others
          and its context was gone, so the analysing session is no longer linked from here */}
      {(isAdmin || a.discuss?.status === 'ready') && <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
        {a.discuss?.status === 'ready' && a.discuss.sessionId ? (
          <>
            <Link to={`/servers/${serverId}/sessions/${a.discuss.sessionId}`} className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>Discuss in the session</Link>
            <span style={{ color: 'var(--color-text-3)' }}>the ticket's own, given the ticket and its analysis</span>
            {isAdmin && <button type="button" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-text-3)' }} disabled={decisions.discuss.isPending} onClick={() => decisions.discuss.mutate(a.key)}>give it the ticket again</button>}
          </>
        ) : a.discuss?.status === 'queued' ? (
          <span style={{ color: 'var(--color-text-3)' }}>Preparing the ticket's session…</span>
        ) : isAdmin ? (
          <button type="button" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }} disabled={decisions.discuss.isPending} onClick={() => decisions.discuss.mutate(a.key)}>
            {decisions.discuss.isPending ? 'Preparing…' : 'Discuss in the session'}
          </button>
        ) : null}
      </p>}
      {a.discuss?.status === 'failed' && a.discuss.error && <p role="alert" style={{ color: 'var(--color-error)' }}>{a.discuss.error}</p>}
      {decisions.discuss.isError && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Not prepared: ${decisions.discuss.error.message}`}</p>}
      {deep?.status === 'queued' && <p style={{ color: 'var(--color-text-3)' }}>{`Deep analysis running, asked ${when(deep.requestedAt)}`}</p>}
      {deep?.status === 'failed' && deep.error && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Deep analysis failed: ${deep.error}`}</p>}
      {decisions.deep.isError && <p role="alert" style={{ color: 'var(--color-error)' }}>{`Deep analysis not started: ${decisions.deep.error.message}`}</p>}
      {deep?.status === 'done' && (
        <Plan id={`${a.key}-plan`} d={deep} isAdmin={isAdmin} discuss={deep.sessionId ? `/servers/${serverId}/sessions/${deep.sessionId}` : undefined}
          onEstimate={isAdmin && open && deep.storyPoints !== undefined ? () => { set('storyPoints', deep.storyPoints); setKept((old) => new Set(old).add('storyPoints')); } : undefined}
          onPost={(comment) => decisions.postPlan.mutate({ key: a.key, comment })}
          posting={decisions.postPlan.isPending} postError={decisions.postPlan.isError ? decisions.postPlan.error.message : undefined}
          implement={{
            pending: decisions.implement.isPending, error: decisions.implement.isError ? decisions.implement.error.message : undefined,
            started: decisions.implement.isSuccess ? `/servers/${serverId}/tickets/${encodeURIComponent(`jira:ticket/${a.site}/${a.key}`)}` : undefined,
            onImplement: (repository, approach) => decisions.implement.mutate({ key: a.key, repository, approach }),
          }} />
      )}
      {/* After the reply: on a mention, the reply is most often all it needs */}
      {(a.infraTicket || a.linkedCreated) && <LinkedTicketForm serverId={serverId} a={a} isAdmin={isAdmin} />}
    </div>
  );
}

/** One ticket: two lines (what it is, who and where), what is proposed on the right, opened to decide; opened and
 * brought into view when a link points at it */
function AnalysisRow({ serverId, a, isAdmin, sprints, focused, now, urgentPriority }: { serverId: string; a: JiraAnalysis; isAdmin: boolean; sprints: string[]; focused: boolean; now: number; urgentPriority?: number }) {
  const ref = useRef<HTMLLIElement>(null);
  const d = digest(a);
  const urgent = groupOf(a) === 'decided' ? [] : urgencyOf(a.urgency, now, urgentPriority).reasons;
  const sprint = sprintOf(a);
  const created = a.ticketCreated && !Number.isNaN(Date.parse(a.ticketCreated)) ? `, created ${new Date(a.ticketCreated).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : a.reporter ? ', created' : '';
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView?.({ block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focused]);
  return (
    <li ref={ref} id={`analysis-${a.key}`} className="border-t" style={{ borderColor: 'var(--color-border)' }} data-testid="analysis">
      {/* Mounted again when a link points at it, so it opens even after the operator closed it */}
      <details key={focused ? 'focused' : 'rest'} open={focused || undefined} className="group">
        <summary className="grid cursor-pointer list-none gap-x-6 gap-y-0.5 rounded-sm py-2 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 sm:grid-cols-[minmax(0,1fr)_minmax(10rem,18rem)] [&::-webkit-details-marker]:hidden"
          style={{ outlineColor: 'var(--color-brand)' }}>
          <span className="min-w-0">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <a href={a.url} target="_blank" rel="noreferrer" className="font-medium tabular-nums underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{a.key}</a>
              <span className="min-w-0 break-words" style={{ color: 'var(--color-text)' }}>{a.title}</span>
            </span>
            <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
              {urgent.length > 0 && <span className="font-medium" style={{ color: 'var(--color-warn)' }} data-testid="analysis-urgency">{urgent.join(', ')}</span>}
              <span>{REASONS[a.reason]}{created}{whoLine(a)}</span>
              {sprint && (
                <span className="rounded border px-1.5" data-testid="analysis-sprint"
                  style={a.inSprint && !a.inSprint.coming ? { borderColor: 'var(--color-brand)', color: 'var(--color-text-2)' } : { borderColor: 'var(--color-border)' }}>{sprint}</span>
              )}
            </span>
          </span>
          <span className="text-xs sm:pt-0.5 sm:text-right" style={{ color: d.tone }}>{d.text}</span>
        </summary>
        {a.status !== 'queued' && <Decision serverId={serverId} a={a} isAdmin={isAdmin} sprints={sprints} />}
      </details>
    </li>
  );
}

/**
 * One Jira ticket on its own page (ADR-039 section 7): what is asked (the ticket, who asked, since when), then the
 * analysis with its decision, the deep plan and the linked ticket, as on the Sprint tab
 */
export function JiraTicket({ serverId, a, isAdmin, sprints, now, urgentPriority }: {
  serverId: string; a: JiraAnalysis; isAdmin: boolean; sprints: string[]; now: number; urgentPriority?: number;
}) {
  const urgent = groupOf(a) === 'decided' ? [] : urgencyOf(a.urgency, now, urgentPriority).reasons;
  const sprint = sprintOf(a);
  const created = a.ticketCreated && !Number.isNaN(Date.parse(a.ticketCreated)) ? `, created ${new Date(a.ticketCreated).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : '';
  const d = digest(a);
  return (
    <article aria-labelledby={`jira-${a.key}-title`} className="card min-w-0 px-4 py-4 hover:transform-none sm:px-5">
      <header className="min-w-0">
        <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>{a.key} (Jira)</p>
        <h3 id={`jira-${a.key}-title`} className="mt-1 max-w-[72ch] text-base font-semibold leading-snug" style={{ color: 'var(--color-text)' }}>
          <a href={a.url} target="_blank" rel="noreferrer" className="break-words hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
            {a.title}<span className="sr-only"> (opens Jira in a new tab)</span>
          </a>
        </h3>
        <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
          {urgent.length > 0 && <span className="font-medium" style={{ color: 'var(--color-warn)' }}>{urgent.join(', ')}</span>}
          <span>{REASONS[a.reason]}{created}{whoLine(a)}</span>
          {sprint && <span className="rounded border px-1.5" style={{ borderColor: 'var(--color-border)' }}>{sprint}</span>}
          <span style={{ color: d.tone }}>{d.text}</span>
        </p>
      </header>
      <div className="mt-4">
        {a.status === 'queued'
          ? <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>The board&apos;s session is analysing it; the analysis comes here.</p>
          : <Decision serverId={serverId} a={a} isAdmin={isAdmin} sprints={sprints} />}
      </div>
    </article>
  );
}

/** The board's incoming tickets, grouped by what the operator does with them: apply what is clear, ask about what is
 * not; then what is being analysed and, folded, what was decided in the last week */
export function IncomingTickets({ serverId, analyses, isAdmin, sprints, now, title = 'Incoming tickets', urgentPriority }: { serverId: string; analyses: JiraAnalysis[]; isAdmin: boolean; sprints: string[]; now: number; title?: string; urgentPriority?: number }) {
  // A notification, a row of the backlog or of the sprint opens one by `?analysis=<key>`
  const [params] = useSearchParams();
  const focus = params.get('analysis');
  // Before the first read (now = 0), nothing counts as decided this week
  const week = now ? now - 7 * 86_400_000 : Infinity;
  const shown = analyses.filter((a) => ['queued', 'pending', 'failed', 'applying'].includes(a.status)
    || (a.status === 'applied' && Date.parse(a.decidedAt ?? a.createdAt) > week));
  if (!shown.length) return null;
  const toDecide = analyses.filter((a) => a.status === 'pending' || a.status === 'failed').length;
  const grouped = new Map<Group, JiraAnalysis[]>();
  for (const a of shown) {
    const g = groupOf(a);
    grouped.set(g, [...(grouped.get(g) ?? []), a]);
  }
  // In each group the most urgent first (expedite, a due date close or passed, the priority); the newest next
  for (const [g, list] of grouped) {
    if (g !== 'decided') grouped.set(g, [...list].sort((x, y) => byUrgency(urgencyOf(x.urgency, now, urgentPriority), urgencyOf(y.urgency, now, urgentPriority))));
  }
  const rows = (list: JiraAnalysis[]) => (
    <ul>{list.map((a) => <AnalysisRow key={a.key} serverId={serverId} a={a} isAdmin={isAdmin} sprints={sprints} focused={a.key === focus} now={now} urgentPriority={urgentPriority} />)}</ul>
  );
  return (
    <section className="mb-6 max-w-5xl" aria-label={title}>
      <div className="flex flex-wrap items-baseline gap-x-4">
        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>{title}</h3>
        <span className="text-sm" style={{ color: 'var(--color-text-2)' }}>{toDecide ? `${toDecide} to decide` : 'nothing to decide'}</span>
      </div>
      {GROUPS.map(({ id, title: heading }) => {
        const list = grouped.get(id) ?? [];
        if (!list.length) return null;
        const label = <><span style={{ color: 'var(--color-text)' }}>{heading}</span> <span className="tabular-nums" style={{ color: 'var(--color-text-3)' }}>{list.length}</span></>;
        // What was decided stays folded unless a link points into it
        if (id === 'decided') {
          return (
            <details key={id} className="mt-4" role="region" aria-label={heading} open={list.some((a) => a.key === focus) || undefined}>
              <summary className="cursor-pointer pb-1 text-sm font-medium">{label}</summary>
              {rows(list)}
            </details>
          );
        }
        return (
          <section key={id} className="mt-4" aria-label={heading}>
            <h4 className="pb-1 text-sm font-medium">{label}</h4>
            {rows(list)}
          </section>
        );
      })}
    </section>
  );
}

/** On a row of the backlog or of the sprint's tickets: the ticket's analysis, or Analyse for an admin */
export function TicketAnalysis({ serverId, boardId, ticketKey, analysis, isAdmin }: {
  serverId: string; boardId: number; ticketKey: string; analysis?: JiraAnalysis; isAdmin: boolean;
}) {
  const decisions = useAnalysisDecisions(serverId);
  if (analysis && analysis.status !== 'dismissed') {
    const v = verdict(analysis);
    return (
      <Link to={`?analysis=${encodeURIComponent(ticketKey)}`} replace preventScrollReset
        className="text-xs underline-offset-2 hover:underline" style={{ color: v.tone }}>{v.text}</Link>
    );
  }
  if (!isAdmin) return null;
  return (
    <span className="flex flex-wrap items-baseline gap-x-2">
    <button type="button" className="text-xs underline-offset-2 hover:underline disabled:opacity-60" style={{ color: 'var(--color-brand-light)' }}
      disabled={decisions.analyse.isPending} onClick={() => decisions.analyse.mutate({ key: ticketKey, boardId })} aria-label={`Analyse ${ticketKey}`}>
      {decisions.analyse.isPending ? 'Sending…' : decisions.analyse.isError ? 'Analyse again' : 'Analyse'}
    </button>
    {decisions.analyse.isError && <span role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>{`Not sent: ${decisions.analyse.error.message}`}</span>}
    </span>
  );
}
